'use client'

import { type ComponentProps, type ReactNode, useId } from 'react'
import { buttonVariants } from './button'
import { cn } from './cn'
import { Spinner } from './spinner'

export interface FilePickerProps
  extends Omit<ComponentProps<'input'>, 'type' | 'className' | 'children'> {
  /** The button's words. An `aria-label` given for context must contain them (WCAG 2.5.3). */
  label: ReactNode
  icon?: ReactNode
  hint?: ReactNode
  error?: string | null
  /** A spinner in place of the icon while the chosen file is read or sent. */
  busy?: boolean
  /** Beside the button: the chosen file's name, or the words for none. Omitted, nothing. */
  chosen?: ReactNode
  variant?: 'outline' | 'staff' | 'secondary'
  /** On the button. */
  className?: string
}

/**
 * A file field that is a button in the page's language. The browser's own
 * control reads "Choose File / No file chosen" in the browser's language and
 * looks like a text box. The native input stays inside the label, visually
 * hidden: it is still what the keyboard focuses, what a screen reader names
 * and what opens the camera or the file picker.
 */
export function FilePicker({
  label,
  icon,
  hint,
  error,
  busy = false,
  chosen,
  variant = 'outline',
  className,
  ...input
}: FilePickerProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <label
          className={cn(
            buttonVariants({ variant }),
            'cursor-pointer has-[:focus-visible]:outline-3 has-[:focus-visible]:outline-focus has-[:focus-visible]:outline-offset-2 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60',
            className,
          )}
        >
          {busy ? <Spinner /> : icon}
          <span>{label}</span>
          <input
            id={id}
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
            {...input}
            type="file"
            className="sr-only"
          />
        </label>
        {chosen === undefined ? null : (
          <span className="min-w-0 break-all text-sm text-text-muted">{chosen}</span>
        )}
      </div>
      {hint ? (
        <p id={hintId} className="text-xs text-text-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-sm font-medium text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}
